import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, type OnDestroy, ViewChild } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { FormViewService, IntegrationMappingService, SubmissionService, VehicleListService } from '@xilo-mono/form-contracts';
import { findBestMatch } from '@xilo-mono/form-helper';
import { NgSelectComponent } from '@ng-select/ng-select';
import { delay, startWith, map, ReplaySubject, takeUntil, switchMap, iif, of, debounceTime } from 'rxjs';
import { findOccupations, getIndustries } from '../../common/industry.util';
import { InitialAddress } from '../address-v2/address-v2.component';
import { changeInputCSS } from '../helper/form';

@Component({
  selector: 'xilo-mono-form-viewer-select',
  templateUrl: './select.component.html',
  styleUrls: ['../../form-viewer.component.scss'],
})
export class CustomSelectComponent extends FieldType implements AfterViewInit, OnDestroy {
  isInvalid = false;
  errorMessage = false;
  bodyStyles = [];
  changeInputCSS = changeInputCSS;
  private readonly destroyed$ = new ReplaySubject<boolean>(1);
  @ViewChild(NgSelectComponent) ngSelectComponent?: NgSelectComponent;
  @ViewChild('dropdownPanel') dropdownPanel: ElementRef;
  linkLoading = false;
  public get isMultipleSelect() {
    return this.to?.isMultipleSelect;
  }
  // AddressV2 vars
  newAddressV2 = '';
  initialAddress: InitialAddress;
  companyId:string
  clientId:number

  constructor(
    private submissionService: SubmissionService,
    private integrationMappingService: IntegrationMappingService,
    public formViewService: FormViewService,
    private vehicleListService: VehicleListService,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  get isAddressV2() {
    return this.field.templateOptions?.version === 2;
  }

  isValueInOptions(): boolean {
    const value = this.formControl.value;
    const options = this.to?.options as any[];

    if (!Array.isArray(options)) return false;

    const optionValues = options.map((opt) => opt?.value);
    if (Array.isArray(value)) return value.every((val) => optionValues.includes(val));
    return optionValues.includes(value);
  }

  ngAfterViewInit(): void {
    this.setupField();
  
    if (this.to?.isMultipleSelect) {
      setTimeout(() => {
        const ngSelectEl = this.elementRef.nativeElement.querySelector('.ng-select');
        const input = ngSelectEl?.querySelector('input');
  
        if (input) {
          input.addEventListener('keydown', (event: KeyboardEvent) => {
            const isShiftEnter = event.key === 'Enter' && event.shiftKey;
            const isCmdEnter = event.metaKey && event.key === 'Enter';
            const isCtrlEnter = event.ctrlKey && event.key === 'Enter';
            const isSubmitHotkey = isCmdEnter || isCtrlEnter;
  
            if (isShiftEnter || isSubmitHotkey) {
              event.preventDefault();
              event.stopImmediatePropagation();
  
              if (isSubmitHotkey && this.ngSelectComponent?.isOpen) {
                this.ngSelectComponent.close();
              }
              const customKeyEvent = new KeyboardEvent('keydown', {
                key: 'Enter',
                code: 'Enter',
                shiftKey: isShiftEnter,
                metaKey: isCmdEnter,
                ctrlKey: isCtrlEnter,
                bubbles: true,
                cancelable: true,
              });
  
              this.elementRef.nativeElement.dispatchEvent(customKeyEvent);
            }
          });
        }
      });
    }
  }  

  setupField() {
    this.castValuesAsStrings();
    this.updateInitialAddress();

    switch (true) {
      case this.to.dataKey === 'vehicleYear' && this.to.options?.['length'] === 0:
        this.updateVehicleYears();
        break;
      case this.to.dataKey === 'vehicleMake':
        this.updateVehicleMakes();
        break;
      case this.to.dataKey === 'vehicleModel':
        this.updateVehicleModels();
        break;
      case this.to.dataKey === 'vehicleBodyStyle':
        this.updateVehicleBodyStylesAndVin();
        break;
      case this.to.dataKey === 'industry':
        this.updateIndustries();
        break;
      case this.to.dataKey === 'occupation':
        this.updateOccupations();
        break;
      case this.to.isRepeatSectionSelect && !!this.to.repeatSectionKey:
        this.updateRepeatObjects();
        break;
      default:
        break;
    }
  }

  private updateVehicleYears() {
    iif(
      () => this.to.subType === 'rv',
      this.vehicleListService.getVehicleYears().pipe(
        map((opts) => opts?.map((year) => ({
          label: year.year,
          value: year.year?.toString()
        })).sort((a, b) => b.label - a.label))
      ),
      of(new Date().getFullYear() + 1).pipe(
        map((oneYearFromNow) => {
          const opts = [];
          for (let i = oneYearFromNow; i > 1949; i--) {
            opts.push({ label: i, value: i?.toString() });
          }
          return opts;
        })
      )
    ).pipe(takeUntil(this.destroyed$)).subscribe(
      (opts) => {
        this.to.options = opts;
        this.cdr.detectChanges();
      }, (error) => console.log(error)
    );
  }

  private updateVehicleMakes() {
    const vehicleYearField = this.field.parent.fieldGroup.find((field) => field.templateOptions.dataKey === 'vehicleYear');
    if (vehicleYearField) {
      const vyf = vehicleYearField.formControl;
      vyf.valueChanges.pipe(
        startWith(vyf.value),
        debounceTime(200),
        switchMap((val) => {
          if (val && val !== 'null') {
            return iif(
              () => this.to.subType === 'rv',
              this.vehicleListService.getVehicleMakes('rv', val),
              this.vehicleListService.getEZMakes(val)
            ).pipe(
              map((opts) => opts?.map((make) => ({
                label: make.name ? make.name.trim() : make.trim(),
                value: make.name ? make.name.trim() : make.trim()
              })).sort((a, b) => a.label.localeCompare(b.label)))
            );
          }
          return of([]);
        }),
        takeUntil(this.destroyed$)
      ).subscribe(
        (opts) => {
          this.to.options = opts;
          this.cdr.detectChanges();
        }, (error) => console.log(error)
      );
    }
  }

  private updateVehicleModels() {
    const vehicleYearField = this.field.parent.fieldGroup.find((field) => field.templateOptions.dataKey === 'vehicleYear');
    const vehicleMakeField = this.field.parent.fieldGroup.find((field) => field.templateOptions.dataKey === 'vehicleMake');
    if (vehicleMakeField && vehicleYearField) {
      const vmf = vehicleMakeField.formControl;
      vmf.valueChanges.pipe(
        startWith(vmf.value),
        debounceTime(200),
        switchMap((val) => {
          if (val && val !== 'null') {
            return iif(
              () => this.to.subType === 'rv',
              this.vehicleListService.getVehicleModels('rv', vehicleYearField.formControl.value, val),
              this.vehicleListService.getEZModels(vehicleYearField.formControl.value, val)
            ).pipe(
              map((opts) => opts?.map((model) => ({
                label: model.name ? model.name.trim() : model.trim(),
                value: model.name ? model.name.trim() : model.trim()
              })).sort((a, b) => a.label.localeCompare(b.label)))
            );
          }
          return of([]);
        }),
        takeUntil(this.destroyed$)
      ).subscribe(
        (opts) => {
          this.to.options = opts;
          this.cdr.detectChanges();
        }, (error) => console.log(error)
      );
    }
  }

  private updateVehicleBodyStylesAndVin() {
    const vehicleYearField = this.field.parent.fieldGroup.find((field) => field.templateOptions.dataKey === 'vehicleYear');
    const vehicleMakeField = this.field.parent.fieldGroup.find((field) => field.templateOptions.dataKey === 'vehicleMake');
    const vehicleModelField = this.field.parent.fieldGroup.find((field) => field.templateOptions.dataKey === 'vehicleModel');
    const vehicleBodyStyleField = this.field.parent.fieldGroup.find((field) => field.templateOptions.dataKey === 'vehicleBodyStyle');
    const vinField = this.field.parent.fieldGroup.find((field) => field.templateOptions.dataKey === 'vin');
    if (vehicleMakeField && vehicleYearField && vehicleModelField) {
      const vmof = vehicleModelField.formControl;
      vmof.valueChanges.pipe(
        startWith(vmof.value),
        debounceTime(200),
        delay(500),
        switchMap((val) => {
          if (val && val !== 'null') {
            try {
              const options = vehicleModelField.templateOptions?.options as any[];
              if (options && options.length > 0) {
                const isExactMatch = options.some((opt) => opt.value === val);
                if (!isExactMatch) {
                  const { success, value } = this.findAndTransformOptionValue(val, options);
                  if (success) {
                    val = value;
                    vehicleModelField.formControl.patchValue(val);
                  }
                }
                return iif(
                  () => this.to.subType === 'rv',
                  this.vehicleListService.getVehicleBodyStyles('rv', vehicleYearField.formControl.value, vehicleMakeField.formControl.value, val),
                  this.vehicleListService.getEZSubModels(vehicleYearField.formControl.value, vehicleMakeField.formControl.value, val)
                ).pipe(
                  map((opts) => {
                    this.bodyStyles = opts;
                    return opts?.map((bodyStyle) => ({
                      label: bodyStyle.name ? bodyStyle.name.trim() : bodyStyle.split('|')[0].trim(),
                      value: bodyStyle.name ? bodyStyle.name.trim() : bodyStyle.split('|')[0].trim()
                    })).sort((a, b) => a.label.localeCompare(b.label));
                  })
                );
              }
            } catch (error) {
              console.log(error);
            }
          }
          return of([]);
        }),
        takeUntil(this.destroyed$)
      ).subscribe(
        (opts) => {
          this.to.options = opts;
          if (vehicleBodyStyleField && opts && opts.length > 0) {
            if (!vehicleBodyStyleField.formControl.value) {
              vehicleBodyStyleField.formControl.patchValue(opts[0]?.value);
              this.cdr.detectChanges();
            }
          }
        }, (error) => console.log(error)
      );
      if (vinField && vehicleBodyStyleField) {
        const vinF = vinField.formControl;
        const vbsF = vehicleBodyStyleField.formControl;
        vbsF.valueChanges.pipe(
          startWith(vbsF.value),
          debounceTime(200),
          map((val) => {
            if (val && val !== 'null') {
              const vinBodyStyle = this.bodyStyles.find((bs) => bs.includes(val));
              if (vinBodyStyle && !vinF.value) {
                const suggestedVin = vinBodyStyle ? vinBodyStyle.split('|')[1].trim() : null;
                vinF.patchValue(suggestedVin);
              }
            }
          }),
          takeUntil(this.destroyed$)
        ).subscribe();
      }
    }
  }

  private updateIndustries() {
    this.to.options = getIndustries(this.to.industryMapVendor).map((opt) => ({
      label: opt.label,
      value: opt.value,
    }));
  }

  private updateOccupations() {
    const industryField = this.field.parent.fieldGroup.find((field) => {
      return field.templateOptions.dataKey === 'industry';
    });
    if (industryField) {
      const indFieldControl = industryField.formControl;
      indFieldControl.valueChanges
        .pipe(
          startWith(indFieldControl.value),
          debounceTime(200),
          map((val) => {
            if (val) {
              this.to.options = findOccupations(
                val,
                this.to.industryMapVendor
              ).map((opt) => ({
                label: opt,
                value: opt,
              }));
            }
          }),
          takeUntil(this.destroyed$)
        )
        .subscribe();
    }
  }

  private updateRepeatObjects() {
    const repeatSection = this.getRepeatSectionByKey(
      this.to.repeatSectionKey,
      this.field.parent
    );

    if (repeatSection) {
      const repeatForm = repeatSection.formControl;
      const repeatModel = repeatForm.value;
      if (Array.isArray(repeatModel)) {
        repeatForm.valueChanges
          .pipe(
            startWith(repeatForm.value),
            debounceTime(200),
            map((val: any[]) => {
              if (val && val.length && val.length > 0) {
                this.to.options = val.map((item, i) => {
                  const newIndex = i + 1;
                  const newItem: { label: any; value: any } = {
                    label: `${this.to.repeatSectionLabel} ${newIndex}`,
                    value: newIndex.toString(),
                  };
                  for (const [cKey, cValue] of Object.entries(item)) {
                    if (cValue && typeof cValue === 'object') {
                      for (const [ccKey, ccValue] of Object.entries(cValue)) {
                        if (ccKey === this.to.repeatSectionFieldKey) {
                          newItem.label = ccValue;
                        }
                      }
                    } else if (
                      cKey === this.to.repeatSectionFieldKey &&
                      cValue
                    ) {
                      newItem.label = cValue;
                    }
                  }
                  return newItem;
                });
              } else {
                this.to.options = [
                  {
                    label: `${this.to.repeatSectionLabel} 1`,
                    value: 1,
                  },
                ];
              }
            }),
            takeUntil(this.destroyed$)
          )
          .subscribe();
      }
    }
  }

  private updateInitialAddress() {
    this.formViewService.addressV2.pipe(
      takeUntil(this.destroyed$)
    ).subscribe((initialAddress) => {
      this.initialAddress = initialAddress;
    });
  }

  private castValuesAsStrings() {
    // Cast the DP data to string if it's an integer
    this.formControl.valueChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(value => {
      if (!isNaN(value) && !(this.to.dataKey === 'vehicleYear') &&  typeof value === 'number' && value >= 0) {
        this.formControl.setValue(value.toString(), {emitEvent: false});
      }
    });
  }

  findAndTransformOptionValue(value: string, options: any[]) {
    options = options.map((opt) => opt.value);
    const exactMatch = options.find((option) => value === option);
    if (!exactMatch) {
      if (value.length === 1) {
        value += 'X';
      }
      const result = findBestMatch(value, options);
      if (result) {
        const { bestMatch } = result;
        if (bestMatch.target) {
          return { success: true, value: bestMatch.target };
        }
        return { success: false, value };
      }
    } else {
      return { success: true, value: exactMatch };
    }
  }

  getRepeatSectionByKey(key: string, parent: any) {
    if (!parent) return null;
    if (parent.fieldArray) {
      return this.getRepeatSectionByKey(key, parent.parent);
    }

    const index = parent.fieldGroup.findIndex((field) => field.key === key);

    if (index > -1) {
      return parent.fieldGroup[index];
    }

    return this.getRepeatSectionByKey(key, parent.parent);
  }

  handleValueChange(item) {
    const value = this.isMultipleSelect ? item?.map((i) => i.value) : item?.value;
    this.formControl.patchValue(value);
    this.formControl.markAsDirty();
    if (this.to.className !== 'xilo-input') this.formViewService.updateForm.next(true);
    this.integrationMappingService.initDataPull(
      this.to?.pullDataTrigger,
      this.to?.dataPullMappingId,
      this.field?.parent?.parent?.templateOptions?.subIndex
    );
    this.changeAddressMeta();
    if (Array.isArray(this.to.clientTag)) {
      this.to.clientTag.forEach((tag) => {
        this.submissionService.onUpdateMetadata({ key: tag, value }, this.field);
      });
    } else if (this.to.clientTag) {
      this.submissionService.onUpdateMetadata({ key: this.to.clientTag, value }, this.field);
    }
  }

  onSelectKeyDownCapture(event: KeyboardEvent): void {
    const isMac = this.formViewService.isMacUser;
    const isCmdEnter = isMac && event.metaKey && event.key === 'Enter';
    const isCtrlEnter = !isMac && event.ctrlKey && event.key === 'Enter';
    const isShiftEnter = event.shiftKey && event.key === 'Enter';
  
    if (isCmdEnter || isCtrlEnter || isShiftEnter) {
      event.preventDefault();
      event.stopImmediatePropagation();

      const ngSelectEl = this.elementRef.nativeElement.querySelector('ng-select');
      if (ngSelectEl?.classList.contains('ng-select-opened')) {
        const clickTarget = this.elementRef.nativeElement.querySelector('.ng-select-container');
        if (clickTarget) {
          (clickTarget as HTMLElement).click();
        }
      }
    }
  }

  selectClassName(className: string) {
    const isTable = className === 'table-input';
    return isTable ? className : '';
  }

  handleDropdownPosition() {
    const selectRect = this.elementRef.nativeElement.getBoundingClientRect();
    const scrollTop =
      document.documentElement.scrollTop || document.body.scrollTop;
    const offsetTop = selectRect.top + window.pageYOffset;
    const height = selectRect.height;
    const dropdownHeight = this.dropdownPanel
      ? this.dropdownPanel.nativeElement.getBoundingClientRect().height
      : '';
    if (
      offsetTop + height + dropdownHeight + 250 >
      scrollTop + document.documentElement.clientHeight
    ) {
      return 'top';
    } else {
      return 'auto';
    }
  }

  // changeAddressMeta: change the address string with users input changes and call the /submission/update endpoint
  changeAddressMeta() {
    const addressFieldKey = this.field.templateOptions.lockedTo; // unique key identifying the address group
    const address = this.field.parent.fieldGroup.find((field) => {
      // read the current address template
      return field.templateOptions.dataKey === addressFieldKey;
    });
    if (this.isAddressV2 && address.formControl.value != null) {
      const key = this.field.templateOptions.dataKey; // key collects the input values unique key
      const value = this.formControl.value; // new value added yo UI
      if (key.includes('state')) {
        // look for initial value and replace. update addressV2 value
        this.newAddressV2 = address.formControl.value.replace(
          this.initialAddress.state,
          value
        );
        this.formViewService.addressV2.next({
          ...this.initialAddress,
          state: value,
        });
        // update address with newly replaced string
        address.formControl.patchValue(this.newAddressV2);
        // ensure active input has latest changes
        this.formControl.patchValue(value);
      }
    }
  }

  public addTag = (term: string) => {
    return term;
  };

  ngOnDestroy() {
    this.destroyed$.next(true);
  }
}
